/**
 * `EscalationList` — left-hand list section of the HITL escalation
 * review queue (ADR-0094 Slice 3).
 *
 * Mirrors the Mandates list: a full-width search input, a row of status
 * filter pills, and a `Card` of selectable row buttons with a left
 * border-accent keyed to status. Each row shows the order headline, the
 * time-to-timeout countdown (red in the final hour), a PENDING/decision
 * pill, the amber rule-that-fired pill, and a mono kicker carrying the
 * escalation_id + relative created time + timeout state.
 *
 * Loading / empty / error states are owned by the parent (`Resolution`)
 * and passed down so this component is purely presentational over the
 * already-fetched `items`.
 */

import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type {
  EscalationItem,
  EscalationStatusFilter,
} from '../../api/escalations.js';
import { Card, Pill, type PillTone, RelativeTime, Tooltip } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { formatMinor } from '../../lib/format.js';
import {
  formatMoney,
  isRefundSummary,
  refundHeadline,
  statusAccentToken,
  statusPill,
  timeToTimeout,
} from './escalation-format.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

const STATUS_FILTERS: { id: EscalationStatusFilter; label: string; tone: PillTone }[] = [
  { id: 'all', label: 'All', tone: 'neutral' },
  { id: 'pending', label: 'Pending', tone: 'sky' },
  { id: 'approved', label: 'Approved', tone: 'mint' },
  { id: 'rejected', label: 'Rejected', tone: 'coral' },
  { id: 'timed_out', label: 'Timed out', tone: 'amber' },
];

function toneFg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeFg;
    case 'amber': return C.amberFg;
    case 'coral': return C.coralFg;
    case 'sky': return C.skyFg;
    case 'mint': return C.mintFg;
    case 'neutral':
    default:
      return C.text;
  }
}

function toneBg(C: ReturnType<typeof useC>, tone: PillTone): string {
  switch (tone) {
    case 'lime': return C.limeDim;
    case 'amber': return C.amberDim;
    case 'coral': return C.coralDim;
    case 'sky': return C.skyDim;
    case 'mint': return C.mintDim;
    case 'neutral':
    default:
      return 'transparent';
  }
}

export interface EscalationListProps {
  items: readonly EscalationItem[];
  selectedId: string | null;
  onSelect: (item: EscalationItem) => void;
  status: EscalationStatusFilter;
  onChangeStatus: (status: EscalationStatusFilter) => void;
  query: string;
  onChangeQuery: (query: string) => void;
  /** Replaces the row list when there's nothing to show — loading
   *  skeleton, empty state, or error card from the parent. */
  emptyState: React.ReactNode | null;
  /** Deep-link anchor: the row with this escalation_id scroll-centers
   *  itself into view + flashes once when it first renders. Set from the
   *  `?escalation=` param (email CTA / dashboard PendingResolutions row)
   *  so arriving on a queue of 50 lands focus ON the item, not the top. */
  scrollAnchorId?: string | null;
}

export function EscalationList({
  items,
  selectedId,
  onSelect,
  status,
  onChangeStatus,
  query,
  onChangeQuery,
  emptyState,
  scrollAnchorId,
}: EscalationListProps): JSX.Element {
  const C = useC();
  const [searchFocused, setSearchFocused] = useState(false);

  return (
    <div>
      {/* Search input — mirrors the Mandates search field styling. */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div
          className="flex items-center gap-2 px-3 py-2 flex-1 min-w-[200px]"
          style={{
            background: searchFocused ? C.surface2 : C.surface,
            border: `1px solid ${searchFocused ? C.limeFg : C.borderSoft}`,
            borderRadius: 6,
            transition: 'border-color 120ms ease-out, background-color 120ms ease-out',
          }}
        >
          <Search size={14} color={searchFocused ? C.limeFg : C.muted} />
          <input
            value={query}
            onChange={(e) => onChangeQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            placeholder="Search escalation id, mandate, merchant, rule…"
            className="flex-1"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              padding: 0,
            }}
            aria-label="Search escalations"
          />
          {query.length > 0 ? (
            <button
              type="button"
              onClick={() => onChangeQuery('')}
              aria-label="Clear search"
              className="flex items-center justify-center"
              style={{
                width: 18,
                height: 18,
                color: C.muted,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <X size={12} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Status filter pills — mono uppercase, tone-accented when active. */}
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 9,
            color: C.muted,
            letterSpacing: 2,
            flexShrink: 0,
          }}
        >
          STATUS
        </span>
        {STATUS_FILTERS.map((s) => {
          const sel = status === s.id;
          const accent = toneFg(C, s.tone);
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => onChangeStatus(s.id)}
              className="px-3 py-1.5"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                letterSpacing: 1,
                color: sel ? accent : C.muted,
                background: sel && s.tone !== 'neutral' ? toneBg(C, s.tone) : sel ? C.surface2 : 'transparent',
                border: `1px solid ${sel ? (s.tone === 'neutral' ? C.border : accent) : 'transparent'}`,
                borderRadius: 2,
                cursor: 'pointer',
              }}
            >
              {s.label.toUpperCase()}
            </button>
          );
        })}
      </div>

      <Card>
        {emptyState !== null
          ? emptyState
          : items.map((item, i) => (
              <EscalationRow
                key={item.escalation_id}
                C={C}
                item={item}
                selected={item.escalation_id === selectedId}
                anchor={scrollAnchorId != null && item.escalation_id === scrollAnchorId}
                last={i === items.length - 1}
                onSelect={() => onSelect(item)}
              />
            ))}
      </Card>
    </div>
  );
}

function EscalationRow({
  C,
  item,
  selected,
  anchor,
  last,
  onSelect,
}: {
  C: ReturnType<typeof useC>;
  item: EscalationItem;
  selected: boolean;
  anchor: boolean;
  last: boolean;
  onSelect: () => void;
}): JSX.Element {
  const presented = statusPill(item.status);
  const accent = C[statusAccentToken(item.status)];
  // Deep-link anchor — when this is the row the caller navigated to
  // (`?escalation=`), center it in the viewport and flash the accent
  // ring once so the eye lands on it in a long queue. Fires on mount
  // (rows map over already-fetched items, so mounting IS "the row just
  // appeared"); a stable `anchor` keeps the effect one-shot.
  const rowRef = useRef<HTMLButtonElement>(null);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (!anchor) return undefined;
    const reduce =
      typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = rowRef.current;
    // `scrollIntoView` is unimplemented in jsdom — guard so the anchor
    // effect degrades to just the flash under test.
    if (el !== null && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
    }
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot on the anchored row
  }, [anchor]);
  const countdown = timeToTimeout(item.timeout_at);
  const summary = item.summary;
  // Refunds (ADR-0099) carry a CLOSED intent — no cart to summarize. The
  // headline reads the refund triplet instead: reason + signed cap.
  const refund = isRefundSummary(summary);
  const isPending = item.status === 'pending';
  const timeoutColor = isPending ? (countdown.imminent ? C.coralFg : C.muted) : C.dim;

  // Row line 1: product summary + countdown.
  //
  // What the merchant reads first is a product name they recognise, not
  // "3 items". When the cart has multiple lines we lead with the first
  // product's title + "+ N more" (e.g. "Felt Hat + 2 more"); quantity
  // prefix (`N × Title`) only on the single-line case where it fits.
  // Prefer the UCP breakdown's title, fall back to the sanitized summary
  // (SKU + product_titles map), fall back to a bare SKU.
  const lineItems = summary.line_items;
  const breakdown = item.breakdown;
  const productLabel: string = refund
    ? refundHeadline(summary)
    : breakdown !== undefined && breakdown.line_items.length > 0
      ? breakdown.line_items.length === 1
        ? (breakdown.line_items[0]!.quantity > 1
            ? `${breakdown.line_items[0]!.quantity} × ${breakdown.line_items[0]!.title}`
            : breakdown.line_items[0]!.title)
        : `${breakdown.line_items[0]!.title} + ${breakdown.line_items.length - 1} more`
      : lineItems !== undefined && lineItems.length > 1
        ? (() => {
            // ADR-0253 2026-10-27 — prefer the merchant-signed `name`
            // on the line (AP2 CartMandate / UCP checkout) over the
            // catalogue-sourced `product_titles` lookup. Both are
            // non-PII; the signed name is what the merchant agent
            // committed to at mandate time.
            const firstLine = lineItems[0]!;
            const firstTitle = firstLine.name ?? summary.product_titles?.[firstLine.sku] ?? firstLine.sku;
            return `${firstTitle} + ${lineItems.length - 1} more`;
          })()
        : (() => {
            const singleLine =
              lineItems !== undefined && lineItems.length === 1 ? lineItems[0] : undefined;
            const sku = singleLine?.sku ?? summary.sku;
            if (sku === undefined) return summary.action.replace(/_/g, ' ');
            return singleLine?.name ?? summary.product_titles?.[sku] ?? sku;
          })();

  // Row line 2: TOTAL mono · muted "max $X". The total prefers the UCP
  // breakdown (richest), falls back to `quoted_total` (Shopify quote
  // persisted on the held summary). Missing on holds with no price yet;
  // we show just the max then.
  const total_minor: number | null =
    breakdown !== undefined
      ? breakdown.total_minor
      : summary.quoted_total !== undefined
        ? Math.round(summary.quoted_total * 100)
        : null;
  const total_currency = breakdown?.currency ?? summary.currency ?? 'USD';
  const totalLabel = total_minor !== null ? formatMinor(total_minor, total_currency) : null;
  const maxLabel =
    !refund && summary.max_amount !== undefined
      ? formatMoney(summary.max_amount, summary.currency)
      : null;

  return (
    <button
      ref={rowRef}
      type="button"
      onClick={onSelect}
      className="w-full p-4 text-left"
      style={{
        background: selected ? C.surface2 : 'transparent',
        borderLeft: `2px solid ${selected ? accent : 'transparent'}`,
        borderBottom: last ? 'none' : `1px solid ${C.borderSoft}`,
        minHeight: 80,
        // The anchor flash rides an inset ring in the row's status accent;
        // it fades out over 600ms after the 1.4s hold clears `flash`.
        boxShadow: flash ? `inset 0 0 0 2px ${accent}` : 'inset 0 0 0 0 transparent',
        transition: 'background-color 200ms ease-out, box-shadow 600ms ease-out',
      }}
    >
      {/* Line 1 — product summary + countdown. The merchant reads the
          product first; the countdown states deadline-pressure next. */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <span
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {productLabel}
        </span>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: timeoutColor,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {isPending ? countdown.label : 'resolved'}
        </span>
      </div>

      {/* Line 2 — the money. Total first (mono, bold), max in parens. */}
      {totalLabel !== null || maxLabel !== null ? (
        <div
          className="flex items-baseline gap-2 mb-2"
          style={{ fontFamily: monoFont, fontVariantNumeric: 'tabular-nums' }}
        >
          {totalLabel !== null ? (
            <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{totalLabel}</span>
          ) : null}
          {maxLabel !== null ? (
            <span style={{ fontSize: 10, color: C.muted }}>max {maxLabel}</span>
          ) : null}
        </div>
      ) : null}

      {/* Pills — rule that fired, status (when not pending), REFUND flag. */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Pill tone="amber">{summary.failed_rule_id ?? item.rule_id}</Pill>
        {!isPending ? <Pill tone={presented.tone}>{presented.label}</Pill> : null}
        {refund ? <Pill tone="amber">REFUND</Pill> : null}
        {isPending && countdown.imminent ? <Pill tone="coral">TIMEOUT IMMINENT</Pill> : null}
        {/* ops2026 UCP checkout test (2026-10-21). A staff-run test
            held by this merchant's rules shows a neutral pill so the
            operator can tell it apart from a real-looking order. */}
        {item.staff_test === true ? (
          <Tooltip content="Run from Sill's ops console to check this site's checkout. Not counted in your numbers.">
            <Pill tone="neutral">SILL TEST</Pill>
          </Tooltip>
        ) : null}
      </div>

      {/* Line 3 — muted site domain · relative created time. */}
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          wordBreak: 'break-all',
        }}
      >
        {summary.merchant} · <RelativeTime iso={item.created_at} />
      </div>
    </button>
  );
}
